// src/components/CertificatesPage.js
import React, { useState, useEffect } from "react";
import { useNavigate } from "react-router-dom";
import {
    Card,
    Typography,
    List,
    Button,
    Tag,
    Empty,
    Spin,
    Result,
    Skeleton,
    Modal,
    Divider,
    Row,
    Col,
    Space,
} from "antd";
import {
    DownloadOutlined,
    FilePdfOutlined,
    TrophyOutlined,
    CalendarOutlined,
    NumberOutlined,
    BookOutlined,
    EyeOutlined,
    FileProtectOutlined,
} from "@ant-design/icons";
import axios from "axios";
import moment from "moment";
import Cookies from "universal-cookie";
const { Title, Paragraph, Text } = Typography;

const CertificatesPage = () => {
    const cookies = new Cookies();
    const auth = cookies.get("authToken");
    const navigate = useNavigate();
    const [loading, setLoading] = useState(true);
    const [certificates, setCertificates] = useState([]);
    const [error, setError] = useState(null);
    const [previewVisible, setPreviewVisible] = useState(false);
    const [selectedCertificate, setSelectedCertificate] = useState(null);
    const [downloading, setDownloading] = useState(false);

    // โหลดข้อมูลใบประกาศนียบัตร
    useEffect(() => {
        const fetchCertificates = async () => {
            try {
                setLoading(true);
                const response = await axios.get("/api/exam/certificates", {
                    headers: { "auth-token": auth, "Content-Type": "application/json" },
                });

                setCertificates(response.data);
                setLoading(false);
            } catch (error) {
                console.error("Error fetching certificates:", error);
                setError("Failed to load certificates. Please try again.");
                setLoading(false);
            }
        };

        fetchCertificates();
    }, []);

    // แสดงรายละเอียดใบประกาศนียบัตร
    const showCertificateDetails = (certificate) => {
        setSelectedCertificate(certificate);
        setPreviewVisible(true);
    };

    // ปิดรายละเอียดใบประกาศนียบัตร
    const closePreview = () => {
        setPreviewVisible(false);
    };

    // ดาวน์โหลดใบประกาศนียบัตร
    const downloadCertificate = async (certificateId) => {
        try {
            setDownloading(true);
            const response = await axios.get(`/api/exam/download-certificate/${certificateId}`, {
                headers: { "auth-token": auth, "Content-Type": "application/json" },
                responseType: "blob",
            });

            // สร้าง URL สำหรับไฟล์ PDF
            const url = window.URL.createObjectURL(new Blob([response.data]));
            const link = document.createElement("a");
            link.href = url;
            link.setAttribute("download", `certificate-${certificateId}.pdf`);
            document.body.appendChild(link);
            link.click();
            document.body.removeChild(link);

            setDownloading(false);
        } catch (error) {
            console.error("Error downloading certificate:", error);
            setDownloading(false);
        }
    };

    if (loading) {
        return (
            <div style={{ padding: "20px" }}>
                <Card>
                    <Skeleton active paragraph={{ rows: 4 }} />
                </Card>
                <div style={{ marginTop: "20px" }}>
                    <Skeleton active />
                    <Skeleton active />
                </div>
            </div>
        );
    }

    if (error) {
        return (
            <Result
                status="error"
                title="Failed to Load Certificates"
                subTitle={error}
                extra={
                    <Button type="primary" onClick={() => navigate("/dashboard")}>
                        Back to Dashboard
                    </Button>
                }
            />
        );
    }

    return (
        <div style={{ padding: "20px" }}>
            <Card>
                <Title level={2}>
                    <FileProtectOutlined style={{ marginRight: "12px", color: "#faad14" }} />
                    ใบประกาศกิตติคุณ
                </Title>
                <Paragraph style={{ marginBottom: "24px" }}>
                    ท่านสามารถตรวจสอบใบประกาศกิตติคุณของหลักสูตรที่ท่านทำผ่านสำเร็จทั้งหมดได้ที่นี่
                    และสามารถดาวน์โหลดเอกสารใบประกาศกิตติคุณดังกล่าวได้ตลอดเวลา
                </Paragraph>

                {certificates.length === 0 ? (
                    <Empty description="คุณยังไม่ได้รับใบประกาศกิตติคุณ" image={Empty.PRESENTED_IMAGE_SIMPLE}>
                        <Button type="primary" onClick={() => navigate("/course")}>
                            ดูบทเรียน
                        </Button>
                    </Empty>
                ) : (
                    <List
                        grid={{
                            gutter: 16,
                            xs: 1,
                            sm: 1,
                            md: 2,
                            lg: 3,
                            xl: 3,
                            xxl: 4,
                        }}
                        dataSource={certificates}
                        renderItem={(certificate) => (
                            <List.Item>
                                <Card
                                    hoverable
                                    cover={
                                        <div
                                            style={{
                                                height: "160px",
                                                background: "linear-gradient(135deg, #1890ff 0%, #52c41a 100%)",
                                                display: "flex",
                                                flexDirection: "column",
                                                justifyContent: "center",
                                                alignItems: "center",
                                                color: "white",
                                                padding: "20px",
                                            }}
                                        >
                                            <FileProtectOutlined style={{ fontSize: "48px", marginBottom: "12px" }} />
                                            <Text style={{ fontSize: "18px", color: "white", textAlign: "center" }}>
                                                {certificate.course_title}
                                            </Text>
                                        </div>
                                    }
                                    actions={[
                                        <Button
                                            type="text"
                                            icon={<EyeOutlined />}
                                            onClick={() => showCertificateDetails(certificate)}
                                        >
                                            View
                                        </Button>,
                                        <Button
                                            type="text"
                                            icon={<DownloadOutlined />}
                                            onClick={() => downloadCertificate(certificate.id)}
                                            loading={downloading}
                                        >
                                            Download
                                        </Button>,
                                    ]}
                                >
                                    <Card.Meta
                                        title={
                                            <div
                                                style={{
                                                    display: "flex",
                                                    justifyContent: "space-between",
                                                    alignItems: "baseline",
                                                }}
                                            >
                                                <span>Certificate</span>
                                                <Tag color="green">Verified</Tag>
                                            </div>
                                        }
                                        description={
                                            <>
                                                <div style={{ marginTop: "8px", marginBottom: "8px" }}>
                                                    <Text type="secondary">
                                                        <CalendarOutlined style={{ marginRight: "8px" }} />
                                                        Issued: {moment(certificate.issue_date).format("MMMM D, YYYY")}
                                                    </Text>
                                                </div>
                                                <div
                                                    style={{
                                                        whiteSpace: "nowrap",
                                                        overflow: "hidden",
                                                        textOverflow: "ellipsis",
                                                    }}
                                                >
                                                    <Text type="secondary">
                                                        <NumberOutlined style={{ marginRight: "8px" }} />
                                                        ID: {certificate.certificate_number}
                                                    </Text>
                                                </div>
                                            </>
                                        }
                                    />
                                </Card>
                            </List.Item>
                        )}
                    />
                )}
            </Card>

            {/* Modal แสดงรายละเอียดใบประกาศนียบัตร */}
            <Modal
                title={
                    <div style={{ display: "flex", alignItems: "center" }}>
                        <FilePdfOutlined style={{ fontSize: "24px", marginRight: "12px", color: "#f5222d" }} />
                        Certificate Details
                    </div>
                }
                open={previewVisible}
                onCancel={closePreview}
                width={720}
                footer={[
                    <Button key="back" onClick={closePreview}>
                        Close
                    </Button>,
                    <Button
                        key="download"
                        type="primary"
                        icon={<DownloadOutlined />}
                        onClick={() => selectedCertificate && downloadCertificate(selectedCertificate.id)}
                        loading={downloading}
                    >
                        Download PDF
                    </Button>,
                ]}
            >
                {selectedCertificate && (
                    <>
                        <div
                            style={{
                                padding: "20px",
                                background: "linear-gradient(135deg, #f0f5ff 0%, #e6fffb 100%)",
                                borderRadius: "8px",
                                marginBottom: "20px",
                                textAlign: "center",
                            }}
                        >
                            <FileProtectOutlined style={{ fontSize: "48px", color: "#faad14", marginBottom: "16px" }} />
                            <Title level={3}>Certificate of Completion</Title>
                            <Title level={4} style={{ fontWeight: "normal", marginTop: "0" }}>
                                {selectedCertificate.course_title}
                            </Title>
                        </div>

                        <Divider />

                        <Row gutter={[16, 16]}>
                            <Col span={12}>
                                <Space direction="vertical" style={{ width: "100%" }}>
                                    <div>
                                        <Text type="secondary">Certificate ID:</Text>
                                        <Paragraph style={{ marginTop: "4px" }}>
                                            {selectedCertificate.certificate_number}
                                        </Paragraph>
                                    </div>
                                </Space>
                            </Col>
                            <Col span={12}>
                                <Space direction="vertical" style={{ width: "100%" }}>
                                    <div>
                                        <Text type="secondary">Issue Date:</Text>
                                        <Paragraph style={{ marginTop: "4px" }}>
                                            {moment(selectedCertificate.issue_date).format("MMMM D, YYYY")}
                                        </Paragraph>
                                    </div>
                                </Space>
                            </Col>
                        </Row>

                        <Row gutter={[16, 16]} style={{ marginTop: "16px" }}>
                            <Col span={24}>
                                <Text type="secondary">Course:</Text>
                                <Paragraph style={{ marginTop: "4px" }}>
                                    <BookOutlined style={{ marginRight: "8px" }} />
                                    {selectedCertificate.course_title}
                                </Paragraph>
                            </Col>
                        </Row>

                        <div
                            style={{
                                marginTop: "24px",
                                padding: "16px",
                                background: "#f5f5f5",
                                borderRadius: "8px",
                                textAlign: "center",
                            }}
                        >
                            <Paragraph type="secondary">
                                This certificate verifies that you have successfully completed the course and passed the
                                final exam.
                            </Paragraph>
                        </div>
                    </>
                )}
            </Modal>
        </div>
    );
};

export default CertificatesPage;
